/**
 * /work-orders/:id — detalle con líneas y cambio de estado.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Field } from '@/components/ui/field'
import { StatusPill } from '@/components/ui/status-pill'
import { ApiError, deleteWorkOrder, getWorkOrder, patchWorkOrder } from '@/lib/api'

const STATUSES = [
  'Reported',
  'Scheduled',
  'In Progress',
  'Waiting for Parts',
  'Waiting for Approval',
  'Completed',
  'Cancelled',
] as const

export function WorkOrderDetailPage() {
  const { workOrderId } = useParams({ from: '/work-orders/$workOrderId' })
  const navigate = useNavigate()
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: ['work-order', workOrderId],
    queryFn: () => getWorkOrder(workOrderId),
  })

  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Los campos arrancan vacíos y se rellenan al abrir el editor: inicializarlos
  // desde `wo` obligaría a declarar el estado después del return de carga, y los
  // hooks no pueden ir detrás de un return condicional.
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [poNumber, setPoNumber] = useState('')
  const [notes, setNotes] = useState('')

  const mutation = useMutation({
    mutationFn: (status: string) => patchWorkOrder(workOrderId, { status }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['work-order', workOrderId] })
      await qc.invalidateQueries({ queryKey: ['work-orders'] })
    },
  })

  const saveDetails = useMutation({
    mutationFn: () =>
      patchWorkOrder(workOrderId, {
        title,
        description: description || null,
        invoiceNumber: invoiceNumber || null,
        poNumber: poNumber || null,
        notes: notes || null,
      }),
    onSuccess: async () => {
      setEditing(false)
      setError(null)
      await qc.invalidateQueries({ queryKey: ['work-order', workOrderId] })
      await qc.invalidateQueries({ queryKey: ['work-orders'] })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo guardar.'),
  })

  const remove = useMutation({
    mutationFn: () => deleteWorkOrder(workOrderId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['work-orders'] })
      void navigate({ to: '/work-orders' })
    },
    onError: (err) => {
      setConfirmDelete(false)
      setError(err instanceof ApiError ? err.message : 'No se pudo eliminar.')
    },
  })

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Cargando…</p>
  if (query.isError || !query.data) {
    return (
      <Card>
        <EmptyState
          title="Orden no encontrada"
          description={(query.error as Error | null)?.message}
          action={
            <Link to="/work-orders" className="text-sm font-medium text-primary">
              Volver
            </Link>
          }
        />
      </Card>
    )
  }

  const wo = query.data.workOrder
  const items = wo.items ?? []

  return (
    <>
      <div className="mb-3">
        <Link
          to="/work-orders"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Work orders
        </Link>
      </div>

      <PageHeader
        title={wo.workOrderNumber}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            <span>{wo.title}</span>
            <StatusPill status={wo.status} />
            <StatusPill status={wo.priority} />
          </span>
        }
        actions={
          <span className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (!editing) {
                  setTitle(wo.title)
                  setDescription(wo.description ?? '')
                  setInvoiceNumber(wo.invoiceNumber ?? '')
                  setPoNumber(wo.poNumber ?? '')
                  setNotes(wo.notes ?? '')
                }
                setEditing((v) => !v)
                setError(null)
              }}
            >
              <Pencil className="size-4" aria-hidden />
              {editing ? 'Cerrar' : 'Editar'}
            </Button>
            {confirmDelete ? (
              <>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  loading={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  Confirmar
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>
                  No
                </Button>
              </>
            ) : (
              <Button type="button" variant="outline" size="sm" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="size-4" aria-hidden />
                Eliminar
              </Button>
            )}
          </span>
        }
      />

      {error && (
        <p role="alert" className="mb-3 text-sm font-medium text-danger">
          {error}
        </p>
      )}

      {editing && (
        <Card className="mb-4">
          <CardHeader title="Editar orden" />
          <CardBody className="space-y-3">
            <Field label="Título" value={title} onChange={(e) => setTitle(e.target.value)} />
            <Field
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Nº de factura"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
              />
              <Field label="PO" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
            </div>
            <Field label="Notas" value={notes} onChange={(e) => setNotes(e.target.value)} />
            <div className="flex gap-2">
              <Button
                type="button"
                className="flex-1"
                loading={saveDetails.isPending}
                onClick={() => saveDetails.mutate()}
              >
                Guardar cambios
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditing(false)}>
                Cancelar
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Detalle" />
          <CardBody className="space-y-2 text-sm">
            <Row label="Unidad" value={wo.unitNumber} />
            <Row label="Proveedor" value={wo.vendorName ?? '—'} />
            <Row label="Abierta" value={wo.dateOpened} />
            <Row label="Completada" value={wo.dateCompleted ?? '—'} />
            <Row label="Factura" value={wo.invoiceNumber ?? '—'} />
            <Row label="PO" value={wo.poNumber ?? '—'} />
            <Row label="Notas" value={wo.notes ?? wo.description ?? '—'} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Cambiar estado" />
          <CardBody className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <Button
                key={s}
                type="button"
                size="sm"
                variant={wo.status === s ? 'primary' : 'outline'}
                loading={mutation.isPending && mutation.variables === s}
                onClick={() => mutation.mutate(s)}
              >
                {s}
              </Button>
            ))}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Líneas"
          action={
            <span className="text-sm font-medium">
              Total ${wo.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          }
        />
        <CardBody className="divide-y divide-border p-0">
          {items.length === 0 ? (
            <EmptyState className="py-8" title="Sin líneas" description="No hay partes ni labor." />
          ) : (
            items.map((it) => (
              <div
                key={it.id}
                className="flex min-h-[52px] items-center justify-between gap-3 px-4 py-3 text-sm"
              >
                <div className="min-w-0">
                  <p className="font-medium">
                    {it.itemType} · {it.description}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {it.quantity} × ${it.unitCost.toLocaleString()}
                  </p>
                </div>
                <span className="font-medium">${it.lineTotal.toLocaleString()}</span>
              </div>
            ))
          )}
          {items.length > 0 && (
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-muted-foreground">
                Subtotal ${wo.subtotal?.toLocaleString() ?? '0'} · Tax $
                {wo.tax?.toLocaleString() ?? '0'}
              </span>
              <span className="font-semibold">${wo.totalCost.toLocaleString()}</span>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium break-all">{value}</span>
    </div>
  )
}
